import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, getProjectDeployment, rollbackProjectDeployment, type DeploymentImpact, type DeploymentRelease, type ProjectDeploymentDetailResponse } from '../api';
import { Callout, Dialog, Input } from './primitives';
import { Field, LoadingRows } from './ui';
import { DeploymentImpactPanel } from './DeploymentImpactPanel';
import { relativeTime } from './project/projectHealth';
import type { SectionPath } from './project/operationalHealth';

/**
 * RollbackDialog is the one rollback confirmation (UX-06): the deployment
 * detail, a component's history and an incident's suggested action all
 * open it. It names what is live and what the rollback returns to (commit,
 * message, author, age), says what the database classification means for
 * a code-only rollback with the checkpoint when it matters, shows the
 * websites and storefronts affected, and records a reason. The decision is
 * the server's (rollback_eligibility): when it refuses, the dialog says why
 * and offers no confirm button.
 */
export function RollbackDialog({
  deploymentId,
  environmentName,
  sectionPath,
  onClose,
  onRolledBack,
}: {
  deploymentId: string;
  environmentName: string;
  sectionPath?: SectionPath;
  onClose: () => void;
  onRolledBack: () => void;
}) {
  const [detail, setDetail] = useState<ProjectDeploymentDetailResponse | null>(null);
  const [impact, setImpact] = useState<DeploymentImpact | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getProjectDeployment(deploymentId)
      .then((resp) => { if (!cancelled) setDetail(resp); })
      .catch((cause: unknown) => { if (!cancelled) setLoadError(cause instanceof Error ? cause : new Error(String(cause))); });
    api.getRollbackImpact(deploymentId)
      .then((r) => { if (!cancelled) setImpact(r.impact); })
      .catch(() => { if (!cancelled) setImpact(null); });
    return () => { cancelled = true; };
  }, [deploymentId]);

  const eligibility = detail?.rollback_eligibility;
  const allowed = Boolean(eligibility?.allowed);
  const from = detail?.release;
  const to = detail?.rollback_target_release;
  const component = detail?.deployment.component_name || 'the release';

  async function confirm() {
    if (!reason.trim()) {
      setError('Say why you are rolling back; it is recorded in the deployment history.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await rollbackProjectDeployment(deploymentId, reason.trim());
      onRolledBack();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The rollback did not start.');
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      size="md"
      title={`Roll back ${component} in ${environmentName}?`}
      description="Traffic switches back to the earlier release; visitors see its code within seconds."
      onClose={onClose}
      busy={busy}
      onSubmit={(event) => {
        event.preventDefault();
        if (allowed) void confirm();
      }}
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {allowed ? (
            <button type="submit" className="button button--danger" disabled={busy || !reason.trim()} data-testid="rollback-confirm">
              {busy ? 'Rolling back' : to ? `Roll back to ${to.commit_sha.slice(0, 7)}` : 'Roll back'}
            </button>
          ) : null}
        </>
      }
      testId="rollback-dialog"
    >
      {loadError ? (
        <Callout tone="danger" title="The deployment could not be read">
          {loadError.message}
        </Callout>
      ) : !detail ? (
        <LoadingRows label="Reading the releases" rows={3} rowHeight="28px" />
      ) : (
        <>
          <div className="rollback-releases" data-testid="rollback-releases">
            <ReleaseCard label="Live now" release={from} />
            <span className="rollback-releases__arrow" aria-hidden="true">
              →
            </span>
            <ReleaseCard label="Goes back to" release={to} />
          </div>

          {!allowed ? (
            <Callout tone="warning" role="status" title="This deployment cannot be rolled back" testId="rollback-refused">
              {eligibility?.reason ?? 'The server could not decide whether a rollback is safe.'}
              {detail.checkpoint && sectionPath && eligibility?.database_safety !== 'safe' ? (
                <>
                  {' '}
                  <Link to={sectionPath('recovery', { intent: 'test_restore', point: detail.checkpoint.id })}>
                    Open the backup taken before it ({relativeTime(detail.checkpoint.completed_at ?? detail.checkpoint.created_at)})
                  </Link>
                </>
              ) : null}
            </Callout>
          ) : (
            <p className="rollback-database" data-testid="rollback-database">
              <strong>Database:</strong>{' '}
              {eligibility?.db_classification === 'db_change_backward_compatible'
                ? 'this release changed the database compatibly, so the earlier code runs on it. Nothing in the database is undone.'
                : 'this release did not change the database, so the earlier code runs on it unchanged.'}
            </p>
          )}

          <DeploymentImpactPanel
            impact={impact}
            rollback
            heading={impact?.installation ? 'Rolling back the backend affects every website' : impact ? 'Rolling back affects only this frontend' : undefined}
          />

          {error ? (
            <Callout tone="danger" title="The rollback did not start">
              {error}
            </Callout>
          ) : null}

          {allowed ? (
            <Field label="Why are you rolling back?" hint="Required. It is recorded in the deployment history.">
              <Input
                id="rollback-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Checkout fails for guests since this release"
                required
              />
            </Field>
          ) : null}
        </>
      )}
    </Dialog>
  );
}

function ReleaseCard({ label, release }: { label: string; release: DeploymentRelease | undefined }) {
  return (
    <div className="rollback-releases__card">
      <span className="rollback-releases__label">{label}</span>
      {release ? (
        <>
          <strong>
            <code>{release.commit_sha.slice(0, 7)}</code> {release.commit_message ? release.commit_message.split('\n')[0] : ''}
          </strong>
          <span className="muted">
            {[release.commit_author, release.release_tag, relativeTime(release.activated_at ?? release.created_at)].filter(Boolean).join(' · ')}
          </span>
        </>
      ) : (
        <span className="muted">Not known</span>
      )}
    </div>
  );
}
